// Shot recipes (pacing spec §6 C2) — data, not code. Each recipe is a list
// of steps; camera moves and holds take time, screen cues fire instantly at
// the current point in the timeline. timeline.ts compiles a recipe into a
// timeline against the live camera pose. Everything uses what the renderer
// already has (camera controls, lights, the DOM overlay); no video files.

import type { MilestonePayload } from '@aod/protocol';
import type {
  CopyRef,
  FocusRef,
  ParticleKind,
  Recipe,
  RecipeId,
  RecipeStep,
  Vec3,
} from './types.js';

// ── step builders ─────────────────────────────────────────────────────────

/** Fly to `at`, ending `height` above it looking down at `pitch` degrees. */
export const flyTo = (at: FocusRef, height: number, pitch: number, ms: number): RecipeStep => ({
  kind: 'flyTo',
  at,
  height,
  pitch,
  ms,
});
/** Circle `at` by `degrees`, keeping the current distance and height. */
export const orbit = (at: FocusRef, degrees: number, ms: number): RecipeStep => ({
  kind: 'orbit',
  at,
  degrees,
  ms,
});
/** Rise to a wide shot over the whole empire. */
export const pullBack = (ms: number): RecipeStep => ({ kind: 'pullBack', ms });
/** Quick cuts to up to three earlier big moments (victory finale). */
export const highlights = (ms: number): RecipeStep => ({ kind: 'highlights', ms });
export const hold = (ms: number): RecipeStep => ({ kind: 'hold', ms });
export const letterbox = (on: boolean): RecipeStep => ({ kind: 'letterbox', on });
export const banner = (title: CopyRef, subtitle?: CopyRef): RecipeStep =>
  subtitle ? { kind: 'banner', title, subtitle } : { kind: 'banner', title };
export const particles = (kind: ParticleKind): RecipeStep => ({
  kind: 'particles',
  particles: kind,
});
export const timeOfDay = (time: 'golden'): RecipeStep => ({ kind: 'timeOfDay', time });
export const bloomPulse = (): RecipeStep => ({ kind: 'bloomPulse' });
/** A sound cue from src/audio/sounds.ts. */
export const sting = (soundId: string): RecipeStep => ({ kind: 'sting', soundId });

// ── recipes ───────────────────────────────────────────────────────────────

export const RECIPES: Readonly<Record<RecipeId, Recipe>> = {
  // A settlement grows a tier (the spec's worked example): low golden-hour
  // fly-in, a slow 60° orbit while the new buildings stand in the light.
  'city.tier_up': [
    letterbox(true),
    timeOfDay('golden'),
    flyTo('focus', 4.5, 28, 1800),
    sting('fanfare'),
    banner('$title', '$subtitle'),
    particles('sparkles'),
    orbit('focus', 60, 3600),
    hold(1200),
    letterbox(false),
  ],
  'city.first_capture': [
    letterbox(true),
    sting('trumpets'),
    flyTo('focus', 5, 32, 1800),
    banner('$title', '$subtitle'),
    particles('dust'),
    bloomPulse(),
    orbit('focus', 45, 3400),
    hold(1200),
    letterbox(false),
  ],
  'city.capital_captured': [
    letterbox(true),
    timeOfDay('golden'),
    sting('trumpets'),
    flyTo('focus', 6.5, 38, 2000),
    sting('fanfare'),
    banner('$title', '$subtitle'),
    particles('fireworks'),
    bloomPulse(),
    orbit('focus', 90, 4200),
    hold(1400),
    letterbox(false),
  ],
  // A Champion rises (D11): fly to the new champion, a short orbit, roar.
  'legend.summoned': [
    letterbox(true),
    sting('roar'),
    flyTo('focus', 4.5, 30, 1800),
    banner('$title', '$subtitle'),
    particles('sparkles'),
    bloomPulse(),
    orbit('focus', 50, 3200),
    hold(1000),
    letterbox(false),
  ],
  // A Champion falls: a held shot and the banner, no particles.
  'legend.fallen': [
    letterbox(true),
    flyTo('focus', 6, 36, 1800),
    banner('$title', '$subtitle'),
    hold(3400),
    letterbox(false),
  ],
  // Finale, 12–15 s: the capital, a few quick cuts back to the match's big
  // moments, then a pull-back over the whole empire. VictoryBanner /
  // PostVictorySummary take over when it ends.
  'victory.achieved': [
    letterbox(true),
    timeOfDay('golden'),
    sting('fanfare'),
    flyTo('capital', 6, 35, 2200),
    hold(800),
    highlights(1000),
    pullBack(3500),
    sting('trumpets'),
    banner('$title', '$subtitle'),
    particles('fireworks'),
    bloomPulse(),
    hold(5500),
    letterbox(false),
  ],
  // Another player won: same shape, no fireworks.
  'victory.lost': [
    letterbox(true),
    flyTo('capital', 6, 35, 2200),
    hold(800),
    highlights(1000),
    pullBack(3500),
    banner('$title', '$subtitle'),
    particles('dust'),
    hold(5500),
    letterbox(false),
  ],
  // Every other milestone: a short banner beat over a gentle move.
  small: [
    sting('achievement'),
    flyTo('focus', 7, 40, 1500),
    banner('$title', '$subtitle'),
    particles('sparkles'),
    hold(3800),
  ],
  // Several milestones arrived during the cooldown: one banner for all of them.
  montage: [
    sting('achievement'),
    flyTo('focus', 9, 45, 1500),
    banner('$title', '$subtitle'),
    particles('sparkles'),
    hold(4800),
  ],
};

/** Milestones that get their own shot; everything else uses `small`. */
export function recipeIdFor(p: MilestonePayload, localPlayer: number): RecipeId {
  switch (p.milestoneId) {
    case 'city.tier_up':
    case 'city.first_capture':
    case 'city.capital_captured':
    case 'legend.summoned':
    case 'legend.fallen':
      return p.milestoneId;
    case 'victory.achieved':
      return p.playerIndex === localPlayer ? 'victory.achieved' : 'victory.lost';
    default:
      return 'small';
  }
}

/** Total length of a recipe with `highlightCount` victory cuts. */
export function recipeDurationMs(recipe: Recipe, highlightCount = 0): number {
  let ms = 0;
  for (const s of recipe) {
    if (s.kind === 'highlights') ms += s.ms * Math.min(3, highlightCount);
    else if ('ms' in s) ms += s.ms;
  }
  return ms;
}

/** World position the recipe's `focus` steps frame, if the payload has one. */
export function payloadFocus(p: MilestonePayload): Vec3 | null {
  const pos = p.position;
  if (!pos || pos.length !== 3 || !pos.every((n) => Number.isFinite(n))) return null;
  return [pos[0], pos[1], pos[2]];
}
